"use client";

import { UploadThumbnailActions } from "@saas/shared/assets/UploadThumbnailActions";
import { buildDisplayImageUrl } from "@saas/shared/lib/image-url-builder";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { FolderIcon, ImagePlusIcon, VideoIcon } from "lucide-react";
import { type DragEvent, useRef, useState } from "react";
import type { I2VReferenceMedia } from "../hooks/use-image-to-video";
import {
	ReferenceMediaThumb,
	getReferenceMediaKind,
} from "./ReferenceMediaThumb";

const VIDEO_ACCEPT = "video/mp4,video/quicktime,video/webm,.mov,.m4v";
const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";

export interface VideoEditUploadLabels {
	videoTitle: string;
	videoHint: string;
	/** 传完视频后缩略图旁边那两行 */
	videoReadyTitle: string;
	videoReadyHint: string;
	photoTitle: string;
	photoHint: string;
}

/**
 * 改视频页（/genjutsu-ai）的上传区，照 AIReel 拆成两格：上面是要改的视频（必填、只收 1 段），
 * 下面是换进去的人 / 宠物 / 产品照片。文件仍交给工作台同一套 handleReferenceFiles，
 * 这里只按格过滤类型；缺哪格就给哪格打 data-missing-field，点 Create 时闪红框
 */
export function VideoEditUploads({
	video,
	images,
	maxImages,
	labels,
	selectAssetLabel,
	removeVideoLabel,
	processingLabel,
	onFiles,
	onRemoveVideo,
	onPickAsset,
	onPreviewImage,
	onEditImage,
	onRemoveImage,
}: {
	video: I2VReferenceMedia | null;
	images: { previewUrl: string }[];
	maxImages: number;
	labels: VideoEditUploadLabels;
	selectAssetLabel: string;
	removeVideoLabel: string;
	processingLabel: (media: I2VReferenceMedia) => string;
	onFiles: (files: File[]) => void;
	onRemoveVideo: (id: string) => void;
	onPickAsset: () => void;
	onPreviewImage: (index: number) => void;
	onEditImage: (index: number) => void;
	onRemoveImage: (index: number) => void;
}) {
	const videoInputRef = useRef<HTMLInputElement>(null);
	const photoInputRef = useRef<HTMLInputElement>(null);
	const [dragOver, setDragOver] = useState<"video" | "photo" | null>(null);

	const videoFiles = (files: File[]) =>
		files
			.filter((file) => getReferenceMediaKind(file) === "video")
			.slice(0, 1);
	const photoFiles = (files: File[]) =>
		files.filter((file) => getReferenceMediaKind(file) === null);

	const dropProps = (zone: "video" | "photo") => ({
		onDragOver: (event: DragEvent) => {
			event.preventDefault();
			setDragOver(zone);
		},
		onDragLeave: () => setDragOver(null),
		onDrop: (event: DragEvent) => {
			event.preventDefault();
			setDragOver(null);
			const files = Array.from(event.dataTransfer.files);
			onFiles(zone === "video" ? videoFiles(files) : photoFiles(files));
		},
	});

	const emptyZoneClass = (zone: "video" | "photo") =>
		cn(
			// 照工作台原来的上传区：灰底 + 1px 细虚线 + 圆角 16
			"flex w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-3 py-5 text-center transition-colors",
			dragOver === zone
				? "border-blue-400 bg-blue-50"
				: "border-zinc-300 bg-zinc-100 hover:border-zinc-400 hover:bg-zinc-200/60",
		);

	return (
		<div className="flex flex-col gap-2">
			<input
				ref={videoInputRef}
				type="file"
				accept={VIDEO_ACCEPT}
				className="hidden"
				onChange={(event) => {
					onFiles(videoFiles(Array.from(event.target.files ?? [])));
					event.target.value = "";
				}}
			/>
			<input
				ref={photoInputRef}
				type="file"
				multiple
				accept={IMAGE_ACCEPT}
				className="hidden"
				onChange={(event) => {
					onFiles(photoFiles(Array.from(event.target.files ?? [])));
					event.target.value = "";
				}}
			/>

			{video ? (
				<div className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-background p-2">
					<ReferenceMediaThumb
						media={video}
						processingLabel={processingLabel(video)}
						removeLabel={removeVideoLabel}
						onRemove={() => onRemoveVideo(video.id)}
						className="aspect-square w-20 rounded-lg border border-border"
					/>
					<span className="min-w-0 text-sm text-zinc-700">
						<span className="block font-medium">
							{labels.videoReadyTitle}
						</span>
						<span className="block text-xs text-zinc-500">
							{labels.videoReadyHint}
						</span>
					</span>
				</div>
			) : (
				<button
					type="button"
					data-missing-field="image"
					onClick={() => videoInputRef.current?.click()}
					className={emptyZoneClass("video")}
					{...dropProps("video")}
				>
					<VideoIcon className="mb-2 size-6 text-zinc-700" />
					<span className="text-sm font-medium text-zinc-700">
						{labels.videoTitle}
					</span>
					<span className="mt-0.5 text-xs text-zinc-500">
						{labels.videoHint}
					</span>
				</button>
			)}

			{images.length ? (
				<div className="grid grid-cols-4 gap-2">
					{images.map((image, index) => (
						<div
							key={image.previewUrl}
							className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted/20"
						>
							<img
								src={buildDisplayImageUrl(
									image.previewUrl,
									384,
								)}
								alt={`@Image ${index + 1}`}
								className="h-full w-full object-cover"
							/>
							<UploadThumbnailActions
								onPreview={() => onPreviewImage(index)}
								onEdit={() => onEditImage(index)}
								onDelete={() => onRemoveImage(index)}
							/>
						</div>
					))}
					{images.length < maxImages ? (
						<button
							type="button"
							aria-label={labels.photoTitle}
							onClick={() => photoInputRef.current?.click()}
							className={cn(
								"flex aspect-square w-full items-center justify-center rounded-lg border-2 border-dashed text-3xl font-light text-zinc-500",
								dragOver === "photo"
									? "border-blue-400 bg-blue-50"
									: "border-zinc-300 bg-zinc-100 hover:bg-zinc-200",
							)}
							{...dropProps("photo")}
						>
							+
						</button>
					) : null}
				</div>
			) : (
				<div
					{...(video && { "data-missing-field": "image" })}
					className={emptyZoneClass("photo")}
					{...dropProps("photo")}
				>
					<button
						type="button"
						onClick={() => photoInputRef.current?.click()}
						className="flex flex-col items-center"
					>
						<ImagePlusIcon className="mb-2 size-6 text-zinc-700" />
						<span className="text-sm font-medium text-zinc-700">
							{labels.photoTitle}
						</span>
						<span className="mt-0.5 text-xs text-zinc-500">
							{labels.photoHint}
						</span>
					</button>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={onPickAsset}
						className="mt-3 h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3.5 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
					>
						<FolderIcon className="h-3.5 w-3.5" />
						{selectAssetLabel}
					</Button>
				</div>
			)}
		</div>
	);
}
